import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api, { apiError, openProtectedFile } from '../../services/api';
import { useApi } from '../../hooks/useApi';
import StatusPill from '../../components/StatusPill';
import Select from '../../components/ui/Select';
import { CodeChip } from '../../components/ui/CountrySelect';
import { Card, EmptyState, LoadingRows, Notice, PageHeader, SearchInput, Segmented, StatCard } from '../../components/ui/Page';
import { companyDisplayName, date, money, titleCase } from '../../lib/format';
import type { Schedule } from '../Fees';

export type PaymentRow = {
  id: string;
  kind: 'RATING_FEE' | 'SUCCESS_FEE';
  status: 'PENDING' | 'PAID' | 'FAILED' | 'CANCELLED';
  amount: number;
  subtotal: number | null;
  taxName: string | null;
  taxPercent: number;
  taxAmount: number;
  currency: string;
  description: string;
  mode: 'DOMESTIC' | 'INTERNATIONAL';
  invoiceNumber: string | null;
  method: string | null;
  reference: string | null;
  note: string | null;
  providerPaymentId: string | null;
  failureReason: string | null;
  paidAt: string | null;
  createdAt: string;
  company: { id: string; name: string; logicalName?: string | null; legalName: string | null; countryCode: string; status: string };
  paidBy: { name: string | null; email: string } | null;
  funding: { partnerName: string; amount: number; currency: string; fundedAt: string; successFeePercent: number } | null;
};

export type CompanyRef = { id: string; name: string; logicalName?: string | null; legalName: string | null; countryCode: string; status: string; isActive: boolean };

export type PaymentsResponse = {
  gateway: string;
  currencies: string[];
  methods: string[];
  payments: PaymentRow[];
  companies: CompanyRef[];
  schedules: Schedule[];
};

export const METHOD_LABEL: Record<string, string> = {
  RAZORPAY: 'Razorpay',
  BANK_TRANSFER: 'Bank transfer',
  UPI: 'UPI',
  CHEQUE: 'Cheque',
  CASH: 'Cash',
  OTHER: 'Other',
  WAIVED: 'Waived',
};

export const KIND_LABEL = { RATING_FEE: 'Rating fee', SUCCESS_FEE: 'Success fee' } as const;

const STATUS = {
  PAID: { label: 'Successful', tone: 'green' },
  PENDING: { label: 'Pending', tone: 'amber' },
  FAILED: { label: 'Failed', tone: 'rose' },
  CANCELLED: { label: 'Cancelled', tone: 'slate' },
} as const;

export function statusOf(p: Pick<PaymentRow, 'status' | 'amount' | 'method'>) {
  if (p.status === 'PAID' && (p.method === 'WAIVED' || p.amount === 0)) return { label: 'Waived', tone: 'slate' as const };
  return STATUS[p.status];
}

export async function openInvoice(p: Pick<PaymentRow, 'id'>) {
  await openProtectedFile(`/admin/payments/${p.id}/invoice`);
}

function InvoiceButton({ p, onError }: { p: PaymentRow; onError: (msg: string) => void }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-[#f0cfdd] bg-white px-2 py-1 text-[11px] font-semibold text-[#d6006e] hover:bg-[#fdf2f8]"
      onClick={(e) => {
        e.stopPropagation();
        openInvoice(p).catch((err) => onError(apiError(err, 'Could not open the invoice.')));
      }}
      title={`Invoice ${p.invoiceNumber || ''}`}
    >
      <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" aria-hidden>
        <path d="M6 2.5h5.5L15 6v10.5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-13a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M11 2.5V6h4M7.5 10.5h5M7.5 13.5h3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      PDF
    </button>
  );
}

function sumByCurrency(rows: PaymentRow[], pick: (p: PaymentRow) => number) {
  const m = new Map<string, number>();
  rows.forEach((p) => m.set(p.currency, (m.get(p.currency) || 0) + pick(p)));
  return Array.from(m.entries());
}

const joinMoney = (pairs: [string, number][], compact = false) => (pairs.length ? pairs.map(([c, v]) => money(v, c, { compact })).join(' + ') : '—');

export default function PaymentsPage() {
  const navigate = useNavigate();
  const created = (useLocation().state as { notice?: string } | null)?.notice;
  const { data, loading, error, reload } = useApi<PaymentsResponse>('/admin/payments');
  const [view, setView] = useState<'transactions' | 'companies'>('transactions');
  const [status, setStatus] = useState<'all' | 'PAID' | 'PENDING' | 'CANCELLED'>('all');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(created ? { tone: 'success', text: created } : null);
  const payments = useMemo(() => data?.payments || [], [data]);
  const live = payments.filter((p) => p.status !== 'CANCELLED');
  const paid = live.filter((p) => p.status === 'PAID' && p.amount > 0);
  const pending = live.filter((p) => p.status === 'PENDING' || p.status === 'FAILED');

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return payments.filter(
      (p) =>
        (status === 'all' ? p.status !== 'CANCELLED' : status === 'PENDING' ? p.status === 'PENDING' || p.status === 'FAILED' : p.status === status) &&
        (!kind || p.kind === kind) &&
        (!term || [p.company.logicalName, p.company.name, p.company.legalName, p.invoiceNumber, p.reference, p.providerPaymentId].some((v) => v?.toLowerCase().includes(term))),
    );
  }, [payments, status, kind, q]);

  const byCompany = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data?.companies || [])
      .filter((c) => !term || [c.logicalName, c.name, c.legalName].some((v) => v?.toLowerCase().includes(term)))
      .map((c) => {
        const own = live.filter((p) => p.company.id === c.id);
        return { company: c, rating: own.find((p) => p.kind === 'RATING_FEE') || null, success: own.filter((p) => p.kind === 'SUCCESS_FEE') };
      });
  }, [data, live, q]);

  const cancel = async (p: PaymentRow) => {
    const what = p.kind === 'RATING_FEE' ? 'Waive this rating fee? The company’s rating unlocks without payment.' : 'Cancel this success fee invoice?';
    if (!window.confirm(`${what}\n\n${companyDisplayName(p.company)} · ${money(p.amount, p.currency)}`)) return;
    setBusy(p.id);
    setNotice(null);
    try {
      await api.patch(`/admin/payments/${p.id}`, { action: 'CANCEL' });
      setNotice({ tone: 'success', text: `${KIND_LABEL[p.kind]} for ${companyDisplayName(p.company)} ${p.kind === 'RATING_FEE' ? 'waived' : 'cancelled'}.` });
      await reload();
    } catch (e) {
      setNotice({ tone: 'error', text: apiError(e, 'Could not update the payment.') });
    } finally {
      setBusy(null);
    }
  };

  const showError = (text: string) => setNotice({ tone: 'error', text });

  return (
    <div>
      <PageHeader
        title="Payments"
        description="Successful and pending fee transactions — rating fee (invoice before rating) and success fee (invoice after funding). Record bank transfers with Create payment."
        actions={
          <Link to="/payments/new" className="mz-btn-primary">
            + Create payment
          </Link>
        }
      />

      <div className="mz-fade-up grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard accent label="Collected" value={<span className="text-lg">{joinMoney(sumByCurrency(paid, (p) => p.amount), true)}</span>} hint={`${paid.length} successful payment${paid.length === 1 ? '' : 's'}`} />
        <StatCard label="Pending" value={<span className="text-lg">{joinMoney(sumByCurrency(pending, (p) => p.amount), true)}</span>} hint={`${pending.length} open invoice${pending.length === 1 ? '' : 's'}`} />
        <StatCard
          label="Tax collected"
          value={<span className="text-lg">{joinMoney(sumByCurrency(paid.filter((p) => p.taxAmount > 0), (p) => p.taxAmount), true)}</span>}
          hint="Owed to tax authorities"
        />
        <StatCard
          label="Funded companies billed"
          value={new Set(live.filter((p) => p.kind === 'SUCCESS_FEE').map((p) => p.company.id)).size}
          hint={`of ${(data?.companies || []).length} companies`}
        />
      </div>

      {(notice || error) && (
        <Notice tone={notice?.tone || 'error'} onClose={() => setNotice(null)} className="mt-4">
          {notice?.text || error}
        </Notice>
      )}

      <Card className="mz-fade-up mz-d1 mt-4" padded={false}>
        <div className="flex flex-wrap items-center gap-2 border-b border-[#fbeef4] p-3">
          <Segmented
            value={view}
            onChange={setView}
            options={[
              { value: 'transactions', label: 'Transactions', count: live.length },
              { value: 'companies', label: 'Invoices by company', count: (data?.companies || []).length },
            ]}
          />
          <SearchInput value={q} onChange={setQ} placeholder={view === 'transactions' ? 'Search company, invoice no. or reference…' : 'Search company…'} className="min-w-[220px] flex-1" />
          {view === 'transactions' && (
            <>
              <Select
                value={kind}
                onChange={setKind}
                className="w-44"
                options={[
                  { value: '', label: 'All fees' },
                  { value: 'RATING_FEE', label: 'Rating fee', description: 'Invoice 1 — before the rating' },
                  { value: 'SUCCESS_FEE', label: 'Success fee', description: 'Invoice 2 — after funding' },
                ]}
              />
              <Segmented
                value={status}
                onChange={setStatus}
                options={[
                  { value: 'all', label: 'All' },
                  { value: 'PAID', label: 'Successful', count: live.filter((p) => p.status === 'PAID').length },
                  { value: 'PENDING', label: 'Pending', count: pending.length },
                  { value: 'CANCELLED', label: 'Cancelled' },
                ]}
              />
            </>
          )}
        </div>

        {loading && !data ? (
          <LoadingRows rows={5} />
        ) : view === 'transactions' ? (
          shown.length === 0 ? (
            <EmptyState
              title={payments.length ? 'No transactions match' : 'No transactions yet'}
              description={payments.length ? 'Adjust the filters to see more.' : 'Rating fee invoices are raised automatically; record bank transfers with Create payment.'}
              action={<Link to="/payments/new" className="mz-btn-primary">+ Create payment</Link>}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="mz-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Invoice</th>
                    <th>Fee</th>
                    <th className="text-right">Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((p) => {
                    const st = statusOf(p);
                    const open = p.status === 'PENDING' || p.status === 'FAILED';
                    return (
                      <tr key={p.id} className={p.status === 'CANCELLED' ? 'opacity-55' : ''}>
                        <td>
                          <div className="flex items-center gap-2">
                            <CodeChip code={p.company.countryCode} />
                            <div className="min-w-0">
                              <p className="max-w-[260px] truncate font-semibold">{companyDisplayName(p.company)}</p>
                              <p className="text-[11px] text-[#a77d92]">{titleCase(p.company.status)}</p>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap font-mono text-[11.5px] text-[#7a1f4a]">{p.invoiceNumber || '—'}</td>
                        <td className="text-[13px]">
                          {KIND_LABEL[p.kind]}
                          <p className="max-w-[240px] truncate text-[11px] text-[#a77d92]" title={p.description}>
                            {p.funding ? `${p.funding.successFeePercent}% of ${money(p.funding.amount, p.funding.currency, { compact: true })} · ${p.funding.partnerName}` : p.kind === 'RATING_FEE' ? 'Before rating' : 'After funding'}
                          </p>
                        </td>
                        <td className="whitespace-nowrap text-right">
                          <p className="font-semibold">{money(p.amount, p.currency)}</p>
                          {p.taxAmount > 0 && (
                            <p className="text-[11px] text-[#a77d92]">
                              incl. {p.taxName} {money(p.taxAmount, p.currency)}
                            </p>
                          )}
                        </td>
                        <td className="text-xs text-[#9a6b82]">
                          {p.status === 'PAID' ? METHOD_LABEL[p.method || 'RAZORPAY'] || p.method : p.mode === 'DOMESTIC' ? 'India · online' : 'Intl card'}
                          {p.reference && <p className="max-w-[160px] truncate font-mono text-[11px]" title={p.reference}>{p.reference}</p>}
                        </td>
                        <td>
                          <StatusPill label={st.label} tone={st.tone} />
                          {open && p.failureReason && <p className="mt-0.5 max-w-[160px] truncate text-[11px] text-[#be123c]" title={p.failureReason}>{p.failureReason}</p>}
                        </td>
                        <td className="whitespace-nowrap text-xs text-[#9a6b82]">
                          {p.status === 'PAID' ? `Paid ${date(p.paidAt)}` : `Raised ${date(p.createdAt)}`}
                          {p.paidBy && p.status === 'PAID' && <p className="text-[11px]">by {p.paidBy.name || p.paidBy.email}</p>}
                        </td>
                        <td>
                          <div className="flex items-center justify-end gap-1.5">
                            {open && (
                              <>
                                <button type="button" className="whitespace-nowrap text-xs font-semibold text-[#047857] hover:underline" onClick={() => navigate(`/payments/new?paymentId=${p.id}`)}>
                                  Record receipt
                                </button>
                                <button type="button" disabled={busy === p.id} className="whitespace-nowrap text-xs font-semibold text-[#9a6b82] hover:underline disabled:opacity-50" onClick={() => void cancel(p)}>
                                  {p.kind === 'RATING_FEE' ? 'Waive' : 'Cancel'}
                                </button>
                              </>
                            )}
                            {p.status !== 'CANCELLED' && <InvoiceButton p={p} onError={showError} />}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : byCompany.length === 0 ? (
          <EmptyState title="No companies" />
        ) : (
          <div className="overflow-x-auto">
            <table className="mz-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Invoice 1 · Rating fee (before rating)</th>
                  <th>Invoice 2 · Success fee (after funding)</th>
                </tr>
              </thead>
              <tbody>
                {byCompany.map(({ company: c, rating, success }) => (
                  <tr key={c.id} className={c.isActive ? '' : 'opacity-60'}>
                    <td>
                      <div className="flex items-center gap-2">
                        <CodeChip code={c.countryCode} />
                        <div className="min-w-0">
                          <p className="max-w-[280px] truncate font-semibold">{companyDisplayName(c)}</p>
                          <p className="text-[11px] text-[#a77d92]">{titleCase(c.status)}</p>
                        </div>
                      </div>
                    </td>
                    <td>
                      {rating ? (
                        <InvoiceCell p={rating} onError={showError} />
                      ) : (
                        <span className="text-xs text-[#a77d92]">No rating fee applies</span>
                      )}
                    </td>
                    <td>
                      {success.length ? (
                        <div className="space-y-1.5">
                          {success.map((p) => (
                            <InvoiceCell key={p.id} p={p} onError={showError} />
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-[#a77d92]">{c.status === 'FUNDED' ? 'Funded — no success fee raised' : 'Raised when funding is recorded'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function InvoiceCell({ p, onError }: { p: PaymentRow; onError: (msg: string) => void }) {
  const st = statusOf(p);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-semibold">{money(p.amount, p.currency)}</span>
      <StatusPill label={st.label} tone={st.tone} />
      <span className="font-mono text-[11px] text-[#a77d92]">{p.invoiceNumber}</span>
      <InvoiceButton p={p} onError={onError} />
    </div>
  );
}
